import { Grid } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { CBAuth, CBCountry, CBCreationOfProps, CBLabel, CBPartnerType, CBValuableLength, CustomerBaseDto, CustomerSearchFilters, ICheckExistenceParams, IFormValues } from "../types";
import CustomerBaseCorporateInfoDetails from "./CustomerBaseCorporateInfoDetails";
import CustomerBaseDetails from "./CustomerBaseDetails";
import CustomerBasePersonalDetails from "./CustomerBasePersonalDetails";
import CBDialogSearchCustomer from "./modal/CBDialogSearchCustomer";
import { useCallback, useState } from "react";
import { getResidenceAddress, isTaxCodeFormatValid } from "../utils";
import { findCustomerBaseByUniqueData, searchCustomerByFilters } from "../api/service/customerbaseApi";
import { useMutation } from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";
import { useHasScope } from "../../../config/globalConfig";
import CustomerBaseInvoicingAddressesDetails from "./CustomerBaseInvoicingAddressesDetails";
import CustomerBaseMainInvoicingAddressDetails from "./CustomerBaseMainInvoicingAddressDetails";
import CustomerBaseSalesAreaDetail from "./salesAreas/CustomerBaseSalesAreaDetail";
import CustomerBasePaymentInfo from "./payments/CustomerBasePaymentInfo";
import CustomerBaseConsentDetails from "./consents/CustomerBaseConsentDetails";

interface CustomerBaseDetailsTabProps extends CBCreationOfProps {
    handleCustomerSelected: (selectedCustomer: CustomerBaseDto) => void
}

const CustomerBaseDetailsTab = ({creationOf, handleCustomerSelected} : CustomerBaseDetailsTabProps) => {

    const hasScope = useHasScope;
    const formContext = useFormContext<IFormValues>();
    const isCreationMode = !!creationOf;
    const partnerType = useWatch<IFormValues, 'cbDetails.partnerType'>({name: "cbDetails.partnerType"});
    const cbCustomerId = useWatch<IFormValues, 'cbDetails.cbCustomerId'>({name: "cbDetails.cbCustomerId"});

    const [openSearchDialog, setOpenSearchDialog] = useState(false);
    const [customerRowData, setCustomerRowData] = useState<any[] | undefined>(undefined);
    const [checkExistenceParamsOld, setCheckExistenceParamsOld] = useState<ICheckExistenceParams>({
        taxCode: '',
        drvLicenceNumber: '',
        vatNumberCountry: '',
        vatNumber: '',
        residenceCountry: '',
        email: ''
    });

    const notAuthorizedToCallCheckCustomerExistence = !hasScope(CBAuth.SCOPE_CREATE_PERSON_CUSTOMER) && !hasScope(CBAuth.SCOPE_CREATE_ORGANIZATION_CUSTOMER);

    const findCustomerByUniqueDataQuery = useMutation({
        mutationFn: (queryBody: CustomerSearchFilters) => {

            return !!queryBody.vatNumber ? searchCustomerByFilters({filters: queryBody, start: "0", limit: "100", orderBy: null}) : findCustomerBaseByUniqueData(queryBody);
        },
        onSuccess: (resp: any) => {
                
            const respData = resp?.data?.data;
            if(!!respData) {
                let data: any[] | undefined = undefined;

                if(Array.isArray(respData)){
                    if(respData.length > 0) 
                        data = respData;

                } else /*if(formContext.getValues("cbDetails.bpCode") !== respData.bpCode)*/ {
                   
                        data = [respData];
                }

                if(!!data) {
                    setCustomerRowData(data);
                    setOpenSearchDialog(true);
                } 
            }
        },
        onError: (err: any) => {
            console.log(err);
        }
    });

    const handleCustomerSelectedAndCheckExistence = useCallback((selectedCustomer: CustomerBaseDto) => {

        handleCustomerSelected(selectedCustomer);

        const residenceAddress = getResidenceAddress(selectedCustomer.contactInfo);

        if(isCreationMode) {
            setCheckExistenceParamsOld({
                taxCode: selectedCustomer?.registry?.taxCode || '',
                drvLicenceNumber: selectedCustomer?.registry?.drvLicenceNumber || '',
                vatNumberCountry: selectedCustomer?.registry?.vatNumberCountry || '',
                vatNumber: selectedCustomer?.registry?.vatNumber || '',
                residenceCountry: residenceAddress?.country || '',
                email: selectedCustomer?.email || '' 
            })
        }

    }, [/*formContext, partnerType, isCreationMode,*/ checkExistenceParamsOld, handleCustomerSelected]);

    const handleCheckCustomerExistenceOnBlur = useCallback(async (labelCheck: string) => {

        if(!isCreationMode) return;
        if(notAuthorizedToCallCheckCustomerExistence) return;

        const { taxCode, residenceCountry, drvLicenceNumber, vatNumberCountry, vatNumber } = formContext.getValues("cbPersonalInfo");
        const email = formContext.getValues("cbDetails.email");

        //Il valore di partnerType non e' sempre popolato, in caso di modifica indirizzo tramite google ritorna vuoto
        const partnerTypeInside = partnerType ? partnerType : formContext.getValues("cbDetails.partnerType") ;

        //console.log('handleCheckCustomerExistenceOnBlur drvLicenceNumber: ', drvLicenceNumber);
        //console.log('handleCheckCustomerExistenceOnBlur partnerTypeInside: ', partnerTypeInside);
        //console.log('handleCheckCustomerExistenceOnBlur partnerType: ', partnerType);
        
        //const drvLicenceNumber = formContext.watch("cbPersonalInfo.drvLicenceNumber");

        if((checkExistenceParamsOld.taxCode?.toUpperCase() == taxCode.toUpperCase()) && (checkExistenceParamsOld.drvLicenceNumber == drvLicenceNumber)
            && (checkExistenceParamsOld.vatNumberCountry == vatNumberCountry) && (checkExistenceParamsOld.vatNumber == vatNumber)
            && (checkExistenceParamsOld.residenceCountry == residenceCountry) && (checkExistenceParamsOld.email == email)) {
                return;
        }

        setCheckExistenceParamsOld({
            taxCode: taxCode.toUpperCase() || '',
            drvLicenceNumber: drvLicenceNumber || '',
            vatNumberCountry: vatNumberCountry || '',
            vatNumber: vatNumber || '',
            residenceCountry: residenceCountry || '',
            email: email || '' 
        });

        var queryBody: CustomerSearchFilters = {
            taxCode: null,
            drvLicenceNumber: null,
            vatNumber: null,
            email: null,
            partnerType: partnerTypeInside
        }


        
        if(partnerTypeInside == CBPartnerType.PERSON) { 
            if(labelCheck == CBLabel.TAX_CODE_LABEL_CHECK && taxCode?.length == CBValuableLength.TAX_CODE && isTaxCodeFormatValid(taxCode)) {
                queryBody = {...queryBody, taxCode: taxCode.toUpperCase()};
            } 
            else if(labelCheck == CBLabel.DRIVING_LICENCE_LABEL_CHECK && !!residenceCountry && residenceCountry !== CBCountry.ITALY) {
                queryBody = {...queryBody, drvLicenceNumber: drvLicenceNumber || null};

            }
        } else {
            if(labelCheck == CBLabel.VAT_NUMBER_LABEL_CHECK && !!vatNumber && !!vatNumberCountry) {
                const isValid = await formContext.trigger(["cbPersonalInfo.vatNumber", "cbPersonalInfo.vatNumberCountry"]);

                queryBody = {...queryBody, vatNumber: isValid ? vatNumberCountry + vatNumber : null};
            } 
            else if(labelCheck == CBLabel.TAX_CODE_LABEL_CHECK && !!taxCode) {

                const isValid = await formContext.trigger("cbPersonalInfo.taxCode");

                queryBody = {...queryBody, taxCode: isValid ? taxCode.toUpperCase() : null};
            }  
        }

        if(labelCheck == CBLabel.EMAIL_LABEL_CHECK && !!email) {
            const isValid = await formContext.trigger("cbDetails.email");
            queryBody = {...queryBody, email: isValid ? email : null};
        }

        //console.log("handleCheckCustomerExistenceOnBlur residenceCountry ", residenceCountry);
        //console.log("handleCheckCustomerExistenceOnBlur labelCheck ", labelCheck);
        //console.log("handleCheckCustomerExistenceOnBlur queryBody ", queryBody);

        if(!!queryBody.taxCode || !!queryBody.drvLicenceNumber || !!queryBody.vatNumber || !!queryBody.email) {
            findCustomerByUniqueDataQuery.mutate(queryBody);
        }
        
    }, [formContext, partnerType, isCreationMode, checkExistenceParamsOld, notAuthorizedToCallCheckCustomerExistence]);

    return (
        <>
            <CBDialogSearchCustomer
                key='cb-check-customer-existence-dialog'
                isPerson={partnerType == CBPartnerType.PERSON}
                open={openSearchDialog} 
                onClose={() => {
                    setOpenSearchDialog(false);
                    setCustomerRowData(undefined);
                }} 
                onCustomerSelected={handleCustomerSelectedAndCheckExistence} 
                customerFilters={null}
                customerData={customerRowData}
            />
            
            <Grid container spacing={2} p={1} alignItems="start"> 
                <Grid item container md={6} xs={12} spacing={2}>
                    <Grid item xs={12}>
                        <CustomerBasePersonalDetails
                            creationOf={creationOf}
                            handleCustomerSelected={handleCustomerSelectedAndCheckExistence}
                            handleCheckCustomerExistenceOnBlur={handleCheckCustomerExistenceOnBlur}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomerBaseDetails
                            creationOf={creationOf}
                            handleCheckCustomerExistenceOnBlur={handleCheckCustomerExistenceOnBlur}
                        />
                    </Grid>
                    {/*<Grid item xs={12}><CustomerBaseTermsAndConditionsDetails/></Grid>*/}

                    <Grid item xs={12}>
                        <CustomerBasePaymentInfo creationOf={creationOf} />
                    </Grid>

                </Grid>
                <Grid item container md={6} xs={12} spacing={2}>
                    {!!cbCustomerId &&
                        <Grid item xs={12}>
                            <CustomerBaseInvoicingAddressesDetails creationOf={creationOf} />
                        </Grid>
                    }
                    {!cbCustomerId &&
                        <Grid item xs={12}>
                            <CustomerBaseMainInvoicingAddressDetails creationOf={creationOf} />
                        </Grid>
                    }
                    {partnerType == CBPartnerType.ORGANIZATION && 
                        <Grid item xs={12}>
                            <CustomerBaseCorporateInfoDetails creationOf={creationOf}/>
                        </Grid>
                    }

                    <Grid item xs={12}>
                        <CustomerBaseSalesAreaDetail creationOf={creationOf} />
                    </Grid>

                    {!isCreationMode && (partnerType == CBPartnerType.PERSON) && 
                        <Grid item xs={12}>
                            <CustomerBaseConsentDetails creationOf={creationOf}/>
                        </Grid>
                    }
                </Grid> 
            </Grid>
            <CustomLoader loading={findCustomerByUniqueDataQuery.isLoading || ( !isCreationMode && !partnerType)} />
        </>
    );
}

export default CustomerBaseDetailsTab;